"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { showcanStsUrl, showcanStsApiKey } from "@/lib/env";

export type ShowcanConversation = {
  role: "user" | "assistant";
  text: string;
  timestamp: string;
};

export type ShowcanStatus =
  | "disconnected"
  | "connecting"
  | "warming_up"
  | "ready"
  | "error";

const useShowcanWs = () => {
  const [status, setStatus] = useState<ShowcanStatus>("disconnected");
  const [isRecording, setIsRecording] = useState(false);
  const [conversation, setConversation] = useState<ShowcanConversation[]>([]);
  const [currentTranscript, setCurrentTranscript] = useState("");
  const [currentLlmText, setCurrentLlmText] = useState("");

  // WebSocket
  const wsRef = useRef<WebSocket | null>(null);

  // Recording (ScriptProcessor approach, matching testui.html)
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  // Flag: mic acquired during connect, waiting for "ready" to start sending
  const pendingAutoRecordRef = useRef(false);

  // Playback – streaming approach matching testui.html
  const playCtxRef = useRef<AudioContext | null>(null);
  const audioFormatRef = useRef<{
    numChannels: number;
    sampleRate: number;
    bitsPerSample: number;
  } | null>(null);
  const nextPlayTimeRef = useRef(0);
  const pcmQueueRef = useRef<Uint8Array[]>([]);
  const pcmQueueLenRef = useRef(0);
  const MIN_SCHEDULE_MS = 150;

  // LLM streaming text accumulation
  const accumulatedLlmTextRef = useRef("");

  // ---------------------------------------------------------------------------
  // Audio streaming playback (matching testui.html)
  // ---------------------------------------------------------------------------

  const scheduleMerged = useCallback(() => {
    if (pcmQueueLenRef.current === 0 || !audioFormatRef.current) return;

    const merged = new Uint8Array(pcmQueueLenRef.current);
    let off = 0;
    for (const c of pcmQueueRef.current) {
      merged.set(c, off);
      off += c.length;
    }
    pcmQueueRef.current = [];
    pcmQueueLenRef.current = 0;

    const { sampleRate, numChannels, bitsPerSample } = audioFormatRef.current;
    const bytesPerFrame = (bitsPerSample / 8) * numChannels;
    const alignedLen =
      Math.floor(merged.length / bytesPerFrame) * bytesPerFrame;

    // Carry over remainder bytes for next schedule
    if (alignedLen < merged.length) {
      const rem = merged.slice(alignedLen);
      pcmQueueRef.current.push(rem);
      pcmQueueLenRef.current = rem.length;
    }

    const pcmData =
      alignedLen < merged.length ? merged.slice(0, alignedLen) : merged;
    const numSamples = Math.floor(pcmData.length / bytesPerFrame);
    if (numSamples <= 0) return;

    const ctx = playCtxRef.current;
    if (!ctx) return;

    try {
      const audioBuffer = ctx.createBuffer(numChannels, numSamples, sampleRate);
      const view = new DataView(
        pcmData.buffer,
        pcmData.byteOffset,
        pcmData.byteLength,
      );
      for (let ch = 0; ch < numChannels; ch++) {
        const channelData = audioBuffer.getChannelData(ch);
        for (let i = 0; i < numSamples; i++) {
          const idx = (i * numChannels + ch) * 2;
          if (idx + 1 < pcmData.length) {
            channelData[i] = view.getInt16(idx, true) / 32768.0;
          }
        }
      }

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);

      const now = ctx.currentTime;
      if (nextPlayTimeRef.current < now) nextPlayTimeRef.current = now;
      source.start(nextPlayTimeRef.current);
      nextPlayTimeRef.current += audioBuffer.duration;
    } catch (e) {
      console.error("Audio playback error:", e);
    }
  }, []);

  const enqueuePcm = useCallback(
    (data: Uint8Array) => {
      pcmQueueRef.current.push(data);
      pcmQueueLenRef.current += data.length;
      if (!audioFormatRef.current) return;
      const bytesPerMs =
        (audioFormatRef.current.sampleRate *
          (audioFormatRef.current.bitsPerSample / 8) *
          audioFormatRef.current.numChannels) /
        1000;
      if (pcmQueueLenRef.current >= bytesPerMs * MIN_SCHEDULE_MS) {
        scheduleMerged();
      }
    },
    [scheduleMerged],
  );

  const collectAudioChunk = useCallback(
    (data: ArrayBuffer) => {
      const arr = new Uint8Array(data);

      if (!audioFormatRef.current) {
        // First chunk: parse WAV header
        if (
          arr.length >= 44 &&
          String.fromCharCode(arr[0], arr[1], arr[2], arr[3]) === "RIFF"
        ) {
          const view = new DataView(
            arr.buffer,
            arr.byteOffset,
            arr.byteLength,
          );
          audioFormatRef.current = {
            numChannels: view.getUint16(22, true),
            sampleRate: view.getUint32(24, true),
            bitsPerSample: view.getUint16(34, true),
          };
          if (!playCtxRef.current) playCtxRef.current = new AudioContext();
          if (arr.length > 44) enqueuePcm(arr.slice(44));
        }
        return;
      }

      enqueuePcm(arr);
    },
    [enqueuePcm],
  );

  const flushAudio = useCallback(() => {
    scheduleMerged(); // Play remaining buffered audio
    audioFormatRef.current = null;
    pcmQueueRef.current = [];
    pcmQueueLenRef.current = 0;
    nextPlayTimeRef.current += 0.2; // 200ms gap between utterances
  }, [scheduleMerged]);

  // ---------------------------------------------------------------------------
  // Internal: activate mic processor using pre-acquired stream
  // Called when "ready" status arrives and mic stream is already held.
  // ---------------------------------------------------------------------------

  const activateRecording = useCallback(() => {
    const stream = mediaStreamRef.current;
    const ws = wsRef.current;
    if (!stream || !ws || ws.readyState !== WebSocket.OPEN) return;

    const audioCtx = new AudioContext({ sampleRate: 16000 });
    audioContextRef.current = audioCtx;

    const source = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;

    const targetRate = 16000;
    const ratio = audioCtx.sampleRate / targetRate;

    processor.onaudioprocess = (e: AudioProcessingEvent) => {
      const input = e.inputBuffer.getChannelData(0);
      const outputLen = Math.floor(input.length / ratio);
      const pcm16 = new Int16Array(outputLen);
      for (let i = 0; i < outputLen; i++) {
        const idx = Math.floor(i * ratio);
        const s = Math.max(-1, Math.min(1, input[idx]));
        pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(pcm16.buffer);
      }
    };

    source.connect(processor);
    processor.connect(audioCtx.destination);

    ws.send(JSON.stringify({ type: "start_recording" }));
    setIsRecording(true);
    pendingAutoRecordRef.current = false;
  }, []);

  // ---------------------------------------------------------------------------
  // Connect (acquires mic permission during user gesture)
  // ---------------------------------------------------------------------------

  const connect = useCallback(
    async () => {
      if (wsRef.current) return;

      if (!showcanStsUrl) {
        console.error("NEXT_PUBLIC_SHOWCAN_STS_URL is not configured");
        setStatus("error");
        return;
      }

      setStatus("connecting");
      setConversation([]);
      setCurrentTranscript("");
      setCurrentLlmText("");
      accumulatedLlmTextRef.current = "";

      // Acquire mic permission NOW (inside user gesture context)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true,
          },
        });
        mediaStreamRef.current = stream;
        pendingAutoRecordRef.current = true;
      } catch (e) {
        console.error("Failed to get microphone:", e);
        setStatus("error");
        return;
      }

      // Normalize URL
      let wsUrl = showcanStsUrl;
      if (!wsUrl.includes("/ws")) {
        wsUrl = wsUrl.replace(/\/$/, "") + "/ws";
      }

      const ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        // TODO: 本番環境ではバックエンドからワンタイムトークンを取得する形に置換予定
        ws.send(
          JSON.stringify({
            type: "auth",
            api_key: showcanStsApiKey,
            prompt_id: "default",
            tts_speaker: "default",
          }),
        );
      };

      ws.onmessage = (event) => {
        // --- Binary: TTS audio data (streaming) ---
        if (event.data instanceof ArrayBuffer) {
          collectAudioChunk(event.data);
          return;
        }

        // --- JSON messages ---
        try {
          const msg = JSON.parse(event.data as string);

          switch (msg.type) {
            case "auth_ok":
              break;

            case "status":
              if (msg.message === "ready") {
                setStatus("ready");
                // Auto-start recording if mic was pre-acquired
                if (pendingAutoRecordRef.current) {
                  activateRecording();
                }
              } else if (msg.message === "warming_up") {
                setStatus("warming_up");
              }
              break;

            case "transcript":
              setCurrentTranscript(msg.text);
              break;

            case "user_text":
              setCurrentTranscript("");
              if (accumulatedLlmTextRef.current) {
                const text = accumulatedLlmTextRef.current;
                accumulatedLlmTextRef.current = "";
                setCurrentLlmText("");
                setConversation((prev) => [
                  ...prev,
                  {
                    role: "assistant",
                    text,
                    timestamp: new Date().toISOString(),
                  },
                ]);
              }
              setConversation((prev) => [
                ...prev,
                {
                  role: "user",
                  text: msg.text,
                  timestamp: new Date().toISOString(),
                },
              ]);
              break;

            case "llm_text":
              setConversation((prev) => [
                ...prev,
                {
                  role: "assistant",
                  text: msg.text,
                  timestamp: new Date().toISOString(),
                },
              ]);
              break;

            case "llm_text_chunk":
              accumulatedLlmTextRef.current += msg.text;
              setCurrentLlmText(accumulatedLlmTextRef.current);
              break;

            case "audio_done":
              flushAudio();
              break;

            case "response_done":
              if (accumulatedLlmTextRef.current) {
                const text = accumulatedLlmTextRef.current;
                accumulatedLlmTextRef.current = "";
                setCurrentLlmText("");
                setConversation((prev) => [
                  ...prev,
                  {
                    role: "assistant",
                    text,
                    timestamp: new Date().toISOString(),
                  },
                ]);
              }
              break;

            case "error":
              console.error("Showcan relay error:", msg.message);
              setStatus("error");
              break;
          }
        } catch (e) {
          console.error("Failed to parse WebSocket message:", e);
        }
      };

      ws.onerror = () => {
        setStatus("error");
      };

      ws.onclose = (event) => {
        if (event.code === 4001) {
          console.error("Authentication failed or timed out");
        }
        if (accumulatedLlmTextRef.current) {
          const text = accumulatedLlmTextRef.current;
          accumulatedLlmTextRef.current = "";
          setCurrentLlmText("");
          setConversation((prev) => [
            ...prev,
            { role: "assistant", text, timestamp: new Date().toISOString() },
          ]);
        }
        wsRef.current = null;
        setStatus("disconnected");
        setIsRecording(false);
      };
    },
    [collectAudioChunk, flushAudio, activateRecording],
  );

  // ---------------------------------------------------------------------------
  // Disconnect
  // ---------------------------------------------------------------------------

  const disconnect = useCallback(() => {
    if (accumulatedLlmTextRef.current) {
      const text = accumulatedLlmTextRef.current;
      accumulatedLlmTextRef.current = "";
      setCurrentLlmText("");
      setConversation((prev) => [
        ...prev,
        { role: "assistant", text, timestamp: new Date().toISOString() },
      ]);
    }

    // Recording cleanup
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    pendingAutoRecordRef.current = false;
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Playback cleanup
    audioFormatRef.current = null;
    pcmQueueRef.current = [];
    pcmQueueLenRef.current = 0;
    nextPlayTimeRef.current = 0;
    if (playCtxRef.current) {
      playCtxRef.current.close();
      playCtxRef.current = null;
    }

    // WebSocket
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }

    setStatus("disconnected");
    setIsRecording(false);
    setCurrentTranscript("");
    setCurrentLlmText("");
    accumulatedLlmTextRef.current = "";
  }, []);

  // ---------------------------------------------------------------------------
  // Manual recording control (for standalone use)
  // ---------------------------------------------------------------------------

  const startRecording = useCallback(async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (isRecording) return;

    // If mic not yet acquired, acquire now
    if (!mediaStreamRef.current) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true,
          },
        });
        mediaStreamRef.current = stream;
      } catch (e) {
        console.error("Failed to get microphone:", e);
        return;
      }
    }

    activateRecording();
  }, [isRecording, activateRecording]);

  const stopRecording = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "stop_recording" }));
    }

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsRecording(false);
  }, []);

  // ---------------------------------------------------------------------------
  // Text message
  // ---------------------------------------------------------------------------

  const sendTextMessage = useCallback((text: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    if (accumulatedLlmTextRef.current) {
      const prev = accumulatedLlmTextRef.current;
      accumulatedLlmTextRef.current = "";
      setCurrentLlmText("");
      setConversation((c) => [
        ...c,
        { role: "assistant", text: prev, timestamp: new Date().toISOString() },
      ]);
    }

    wsRef.current.send(JSON.stringify({ type: "text", text }));
    setConversation((prev) => [
      ...prev,
      { role: "user", text, timestamp: new Date().toISOString() },
    ]);
  }, []);

  // ---------------------------------------------------------------------------
  // Cleanup on unmount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      processorRef.current?.disconnect();
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
      audioContextRef.current?.close();
      playCtxRef.current?.close();
      wsRef.current?.close();
    };
  }, []);

  return {
    status,
    isRecording,
    conversation,
    currentTranscript,
    currentLlmText,
    connect,
    disconnect,
    startRecording,
    stopRecording,
    sendTextMessage,
  };
};

export default useShowcanWs;
